import { useQuery } from '@tanstack/react-query';

import { textractApi } from '@/domain/textract/api';
import type { FieldMapping } from '@/domain/textract/schemas';
import { queryOptions } from '@/services/query';
import { textractKeys } from '@/domain/textract/queries/keys';

/**
 * Deduplicate fields by displayName — keeps the first occurrence.
 * Moved from the former textractFieldsMappingUniqueSelector.
 */
const deduplicateFields = (fields: FieldMapping[]): FieldMapping[] =>
  Object.values(
    fields.reduce<Record<string, FieldMapping>>((acc, field) => {
      if (!acc[field.displayName]) acc[field.displayName] = field;
      return acc;
    }, {}),
  );

export const textractFieldsMappingQuery = (companyId: string | null, documentID: string) =>
  queryOptions({
    queryKey: textractKeys.fieldsMapping(companyId, documentID),
    queryFn: () => textractApi.getFieldsMapping({ path: { documentID } }),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

/**
 * Fetches the catalogue of fields a 2WM table column can be bound to.
 * Returns deduplicated fields (by displayName) via `select`.
 */
export const useTextractFieldsMapping = (companyId: string | null, documentID: string, enabled: boolean) =>
  useQuery({
    ...textractFieldsMappingQuery(companyId, documentID),
    enabled,
    select: deduplicateFields,
  });
